import { useEffect, useState, useCallback } from 'react';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { FileText, AlertCircle, CheckCircle2, Upload, ChevronRight, X } from 'lucide-react';
import { Can } from '../../lib/rbac';

interface MpReport {
  id: number;
  seller_id: number;
  ml_site_id: string | null;
  period_start: string;
  period_end: string;
  filename: string;
  source: string;
  row_count: number;
  total_net_credit: string;
  total_mp_fee: string;
  total_shipping: string;
  discrepancies_found: number;
  total_discrepancy_amount: string;
  status: string;
  received_at: string;
  reconciled_at: string | null;
}

interface ReportDetail {
  report: MpReport & { summary: any; raw_csv_path: string | null };
  discrepancies: Array<{
    id: number;
    ml_order_id: number | null;
    discrepancy_type: string;
    severity: string;
    expected_amount: string;
    actual_amount: string;
    difference: string;
    description: string;
    status: string;
  }>;
  by_description: Array<{ description: string; count: number; total_gross: string }>;
}

const fmt = (n: number | string) => `$${Number(n ?? 0).toFixed(2)}`;

const TYPE_LABEL: Record<string, string> = {
  missing_in_db: 'Falta en DB',
  gross_mismatch: 'Gross mismatch',
  fee_mismatch: 'Fee mismatch',
  shipping_mismatch: 'Shipping mismatch',
  extra_in_csv: 'Extra en CSV',
};

const SEV_COLOR: Record<string, string> = {
  critical: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30',
  warning: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30',
  info: 'bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/30',
};

const STATUS_COLOR: Record<string, string> = {
  open: 'text-amber-600',
  ignored: 'text-gray-500',
  resolved: 'text-green-600',
  pending_action: 'text-blue-600',
};

export function MpReconciliationSection() {
  const sellerId = useUIStore(s => s.sellerId);
  const [reports, setReports] = useState<MpReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [drillId, setDrillId] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<MpReport[]>(`/finance/mp-reports?seller_id=${sellerId}&limit=50`);
      setReports(res.data ?? []);
    } catch (err) {
      console.error('[mp-recon] load:', err);
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/finance/mp-reports/upload?seller_id=${sellerId}&source=manual_upload`, {
        method: 'POST', body: fd, credentials: 'include',
      });
      const json = await res.json();
      if (json.success) {
        alert(`Importado: ${json.data.filename}\nRows: ${json.data.row_count}\nDiscrepancias: ${json.data.summary.discrepancies.total_open}`);
        await load();
      } else {
        alert(`Error: ${json.error}`);
      }
    } catch (err) {
      alert(`Error: ${(err as Error).message}`);
    } finally {
      setUploading(false);
    }
  };

  // Compute aggregates
  const totals = reports.reduce(
    (acc, r) => ({
      reports: acc.reports + 1,
      rows: acc.rows + (r.row_count || 0),
      net_credit: acc.net_credit + Number(r.total_net_credit ?? 0),
      mp_fee: acc.mp_fee + Number(r.total_mp_fee ?? 0),
      shipping: acc.shipping + Number(r.total_shipping ?? 0),
      discrepancies: acc.discrepancies + (r.discrepancies_found || 0),
      diff_amount: acc.diff_amount + Number(r.total_discrepancy_amount ?? 0),
    }),
    { reports: 0, rows: 0, net_credit: 0, mp_fee: 0, shipping: 0, discrepancies: 0, diff_amount: 0 },
  );

  return (
    <div className="space-y-4">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KPICard label="Reports" value={String(totals.reports)} sub={`${totals.rows} releases`} icon={<FileText className="w-4 h-4 text-blue-500" />} />
        <KPICard label="Net credit" value={fmt(totals.net_credit)} sub="liberado" icon={<CheckCircle2 className="w-4 h-4 text-green-500" />} />
        <KPICard label="MP fees" value={fmt(totals.mp_fee)} sub="comisiones" />
        <KPICard label="Shipping" value={fmt(totals.shipping)} sub="costos envío" />
        <KPICard label="Discrepancias" value={String(totals.discrepancies)} sub={fmt(totals.diff_amount)} icon={<AlertCircle className="w-4 h-4 text-amber-500" />} />
      </div>

      {/* Upload + actions */}
      <Can cap="finance.operate">
        <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-3 flex items-center gap-3">
          <label className="flex items-center gap-2 px-3 py-1.5 text-xs bg-[var(--color-primary)]/10 hover:bg-[var(--color-primary)]/20 text-[var(--color-primary)] rounded cursor-pointer transition-colors">
            <Upload className="w-3 h-3" />
            {uploading ? 'Subiendo...' : 'Subir CSV manual'}
            <input type="file" accept=".csv" hidden disabled={uploading}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ''; }} />
          </label>
          <span className="text-xs text-[var(--color-text-secondary)]">
            Reports llegan automático vía webhook MP + SFTP. Subir manual solo para casos especiales.
          </span>
        </div>
      </Can>

      {/* Reports table */}
      <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-[var(--color-border)] text-xs text-[var(--color-text-secondary)]">
          {loading ? 'Cargando...' : `${reports.length} reports`}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-bg-subtle)] text-xs text-[var(--color-text-secondary)] uppercase">
              <tr>
                <th className="text-left px-3 py-2">Site</th>
                <th className="text-left px-3 py-2">Período</th>
                <th className="text-left px-3 py-2">Filename</th>
                <th className="text-right px-3 py-2">Rows</th>
                <th className="text-right px-3 py-2">Net credit</th>
                <th className="text-right px-3 py-2">Discrepancias</th>
                <th className="text-left px-3 py-2">Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {reports.map(r => (
                <tr key={r.id} className="border-t border-[var(--color-border)] hover:bg-[var(--color-bg-subtle)]">
                  <td className="px-3 py-2 text-xs">{r.ml_site_id ?? '—'}</td>
                  <td className="px-3 py-2 text-xs">{r.period_start?.slice(0, 10)} → {r.period_end?.slice(0, 10)}</td>
                  <td className="px-3 py-2 text-xs font-mono truncate max-w-[260px]" title={r.filename}>{r.filename}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{r.row_count}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(r.total_net_credit)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">
                    {r.discrepancies_found > 0 ? (
                      <span className="text-amber-600">{r.discrepancies_found} ({fmt(r.total_discrepancy_amount)})</span>
                    ) : '0'}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span className={r.status === 'reconciled' ? 'text-green-600' : 'text-amber-600'}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => setDrillId(r.id)} className="text-[var(--color-primary)]">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {!reports.length && !loading && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-[var(--color-text-secondary)]">
                  Sin reports todavía. Suben automáticamente vía webhook MP.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {drillId !== null && <ReportDrillModal id={drillId} onClose={() => setDrillId(null)} />}
    </div>
  );
}

function KPICard({ label, value, sub, icon }: { label: string; value: string; sub?: string; icon?: React.ReactNode }) {
  return (
    <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-[var(--color-text-secondary)]">{label}</span>
        {icon}
      </div>
      <div className="text-lg font-bold text-[var(--color-text)]">{value}</div>
      {sub && <div className="text-xs text-[var(--color-text-secondary)]">{sub}</div>}
    </div>
  );
}

function ReportDrillModal({ id, onClose }: { id: number; onClose: () => void }) {
  const [data, setData] = useState<ReportDetail | null>(null);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('open');

  useEffect(() => {
    api.get<ReportDetail>(`/finance/mp-reports/${id}`).then(r => { if (r.data) setData(r.data); });
  }, [id]);

  if (!data) return null;
  const { report, discrepancies, by_description } = data;

  const filtered = discrepancies.filter(d =>
    (!filterType || d.discrepancy_type === filterType) &&
    (!filterStatus || d.status === filterStatus),
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg max-w-5xl w-full max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-[var(--color-bg-card)] border-b border-[var(--color-border)] p-4 flex items-center justify-between">
          <div>
            <h3 className="font-bold">Report #{report.id} — {report.ml_site_id}</h3>
            <p className="text-xs text-[var(--color-text-secondary)]">{report.filename}</p>
          </div>
          <button onClick={onClose} className="text-[var(--color-text-secondary)]"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* Totales */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KPICard label="Net credit" value={fmt(report.total_net_credit)} />
            <KPICard label="MP fees" value={fmt(report.total_mp_fee)} />
            <KPICard label="Shipping" value={fmt(report.total_shipping)} />
            <KPICard label="Discrepancias" value={String(report.discrepancies_found)} sub={fmt(report.total_discrepancy_amount)} />
          </div>

          {/* By description */}
          <div>
            <h4 className="text-sm font-semibold mb-1">Por tipo de transacción</h4>
            <table className="w-full text-xs">
              <thead className="bg-[var(--color-bg-subtle)]">
                <tr>
                  <th className="text-left px-2 py-1">Description</th>
                  <th className="text-right px-2 py-1">Count</th>
                  <th className="text-right px-2 py-1">Total gross</th>
                </tr>
              </thead>
              <tbody>
                {by_description.map(b => (
                  <tr key={b.description} className="border-t border-[var(--color-border)]">
                    <td className="px-2 py-1">{b.description}</td>
                    <td className="px-2 py-1 text-right font-mono">{b.count}</td>
                    <td className="px-2 py-1 text-right font-mono">{fmt(b.total_gross)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Discrepancies */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold">Discrepancias ({filtered.length}/{discrepancies.length})</h4>
              <div className="flex gap-2">
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                  className="px-2 py-1 text-xs bg-[var(--color-bg-subtle)] border border-[var(--color-border)] rounded">
                  <option value="">Todos los status</option>
                  <option value="open">Open</option>
                  <option value="ignored">Ignored</option>
                  <option value="pending_action">Pending action</option>
                  <option value="resolved">Resolved</option>
                </select>
                <select value={filterType} onChange={e => setFilterType(e.target.value)}
                  className="px-2 py-1 text-xs bg-[var(--color-bg-subtle)] border border-[var(--color-border)] rounded">
                  <option value="">Todos los tipos</option>
                  {Object.keys(TYPE_LABEL).map(t => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
                </select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[var(--color-bg-subtle)]">
                  <tr>
                    <th className="text-left px-2 py-1">Order</th>
                    <th className="text-left px-2 py-1">Tipo</th>
                    <th className="text-left px-2 py-1">Sev</th>
                    <th className="text-right px-2 py-1">Expected</th>
                    <th className="text-right px-2 py-1">Actual</th>
                    <th className="text-right px-2 py-1">Diff</th>
                    <th className="text-left px-2 py-1">Status</th>
                    <th className="text-left px-2 py-1">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 200).map(d => (
                    <tr key={d.id} className="border-t border-[var(--color-border)]">
                      <td className="px-2 py-1 font-mono">{d.ml_order_id ?? '—'}</td>
                      <td className="px-2 py-1">{TYPE_LABEL[d.discrepancy_type] ?? d.discrepancy_type}</td>
                      <td className="px-2 py-1">
                        <span className={`px-1.5 py-0.5 text-[10px] border rounded ${SEV_COLOR[d.severity] ?? ''}`}>{d.severity}</span>
                      </td>
                      <td className="px-2 py-1 text-right font-mono">{fmt(d.expected_amount)}</td>
                      <td className="px-2 py-1 text-right font-mono">{fmt(d.actual_amount)}</td>
                      <td className="px-2 py-1 text-right font-mono">{fmt(d.difference)}</td>
                      <td className="px-2 py-1">
                        <span className={STATUS_COLOR[d.status] ?? ''}>{d.status}</span>
                      </td>
                      <td className="px-2 py-1 text-[var(--color-text-secondary)]" title={d.description}>
                        {d.description?.slice(0, 60)}{(d.description?.length ?? 0) > 60 ? '…' : ''}
                      </td>
                    </tr>
                  ))}
                  {filtered.length > 200 && (
                    <tr><td colSpan={8} className="px-2 py-2 text-center text-xs text-[var(--color-text-secondary)]">
                      Mostrando primeras 200 de {filtered.length}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
